import type { Guide } from "@/data/guides";

// Single source of truth for a static guide's JSON-LD, consumed by both
// scripts/prerenderRoutes.ts (build-time, baked into static HTML for non-JS
// crawlers) and any client-side use. Extracted from what was previously
// inline, duplicated logic in GuidePage.tsx so the two can never drift the
// way the homepage's title/schema did before that fix — see
// src/lib/homepageSchema.ts for the same pattern.

const BASE_URL = "https://homeapproach.com";

export function buildGuideJsonLd(guide: Guide) {
  const guideUrl = `${BASE_URL}/guides/${guide.slug}`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.subtitle,
    author: { "@type": "Organization", name: guide.author, url: BASE_URL },
    publisher: {
      "@type": "Organization",
      name: "Home Approach",
      url: BASE_URL,
      logo: { "@type": "ImageObject", url: `${BASE_URL}/og-image.png` },
    },
    mainEntityOfPage: guideUrl,
    articleSection: guide.category,
    wordCount: guide.sections.reduce((sum, s) => sum + s.content.split(/\s+/).length, 0),
  };

  const faqJsonLd =
    guide.faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: guide.faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }
      : null;

  const howToJsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: guide.title,
    description: guide.subtitle,
    step: guide.sections.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.heading,
      text: s.content.slice(0, 300),
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
      { "@type": "ListItem", position: 2, name: "Guides", item: `${BASE_URL}/guides` },
      { "@type": "ListItem", position: 3, name: guide.title, item: guideUrl },
    ],
  };

  return [articleJsonLd, howToJsonLd, breadcrumbJsonLd, ...(faqJsonLd ? [faqJsonLd] : [])];
}
